import { ChevronLeft, CircleHelp, XIcon } from "lucide-react";
import { ReactNode } from "react";
import { Button, ButtonVariant } from "shared/components/Button";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { useEnrollment } from "../providers/enrollment";
import { useIntercom } from "react-use-intercom";
import { useAuth } from "providers/auth";
import { postEvent } from "helpers/postEvent";
import { useLocation } from "react-router-dom";
import { Hide } from "shared/components/helpers/Hide";
import { usePostHog } from "posthog-js/react";

export const Form = ({
  children,
  formId,
  onSubmit,
  submitText,
  isSubmitDisabled,
  isSubmitLoading,
  onBack,
  submitButton
}: {
  children: ReactNode;
  formId?: string;
  onSubmit?: (e: any) => void;
  submitText?: string;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
  onBack?: () => void;
  submitButton?: ReactNode;
}) => {
  const { setPrevView, patchCustomerDetails, patchEnrollmentCase } =
    useEnrollment();
  const { show } = useIntercom();
  const { sdkConfig } = useAuth();
  const { pathname } = useLocation();

  const { featureFlags } = usePostHog();
  const hideSupportOnboarding = Boolean(
    featureFlags?.isFeatureEnabled("hide_support")
  );

  return (
    <div className="flex flex-col h-screen justify-between">
      <div className="md:border-b border-alpha-100 px-6">
        <div className="flex items-center justify-between md:min-h-16 -mx-4 ">
          <IconButton
            Icon={ChevronLeft}
            size={IconButtonSize.BASE}
            onClick={() => {
              if (onBack) onBack();
              else setPrevView();
            }}
            className="md:hidden"
            borderLess
          />
          <div className="flex items-center ml-auto">
            <Hide if={hideSupportOnboarding}>
              <IconButton
                className="shrink-0"
                Icon={CircleHelp}
                size={IconButtonSize.BASE}
                borderLess
                onClick={show}
              />
            </Hide>
            {sdkConfig?.hasClose && (
              <IconButton
                Icon={XIcon}
                className="shrink-0"
                size={IconButtonSize.BASE}
                onClick={() => {
                  postEvent({
                    type: "action",
                    action: "close",
                    source: pathname
                  });
                }}
                borderLess
              />
            )}
          </div>
        </div>
      </div>
      <form
        className="p-6 h-full max-w-[440px] w-full mx-auto animate-appear overflow-y-auto "
        id={formId}
        onSubmit={
          onSubmit
            ? (e) => {
                postEvent({
                  type: "analytics",
                  action: "click",
                  source: pathname,
                  message: "Continue button clicked"
                });
                onSubmit(e);
              }
            : undefined
        }
      >
        {children}
      </form>
      <div className="mt-auto border-t flex justify-between border-alpha-100 py-4 md:mx-0 px-6">
        <Button
          onClick={() => {
            if (onBack) onBack();
            else setPrevView();
          }}
          className="md:block hidden"
        >
          Back
        </Button>

        {submitButton ? (
          submitButton
        ) : (
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-full max-w-[400px] md:w-auto md:min-w-[104px] mx-auto md:mr-0"
            form={formId}
            type="submit"
            disabled={isSubmitDisabled}
            loading={
              patchEnrollmentCase?.isPending ||
              patchCustomerDetails?.isPending ||
              isSubmitLoading
            }
          >
            {submitText || "Continue"}
          </Button>
        )}
      </div>
    </div>
  );
};
